import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao } from '../../componentes/base';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Estado, Vazio } from '../../componentes/estados';
import { Entrada } from '../../componentes/formulario';
import { Confirmar } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { rotuloMotivoPerda } from '../../nucleo/rotulos';
import { useSessao } from '../../nucleo/sessao';
import type { Motivo } from './tipos';

/** M2.6 aba Motivos de perda: lista usada na confirmação de oportunidade perdida. */
// Val5 (29/10): motivos de perda são configuração do CRM (`pipeline.configurar`); a lixeira segue com `pipeline.excluir`.
export function MotivosPerda() {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [nome, setNome] = useState('');
  const [editando, setEditando] = useState<{ id: string; nome: string } | null>(null);
  const [excluindo, setExcluindo] = useState<Motivo | null>(null);
  const q = useQuery({ queryKey: ['pipeline', 'motivos'], queryFn: () => get<{ itens: Motivo[] }>('/pipeline/motivos-perda') });
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pipeline', 'motivos'] });
  const criar = useMutation({
    mutationFn: () => post('/pipeline/motivos-perda', { nome }),
    onSuccess: async () => {
      setNome('');
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const renomear = useMutation({
    mutationFn: (v: { id: string; nome: string }) => patch(`/pipeline/motivos-perda/${v.id}`, { nome: v.nome }),
    onSuccess: async () => {
      setEditando(null);
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const remover = useMutation({
    mutationFn: (id: string) => del(`/pipeline/motivos-perda/${id}`),
    onSuccess: async () => {
      setExcluindo(null);
      toast.sucesso(tn('comum.removido'));
      await recarregar();
    },
    onError: (e) => {
      setExcluindo(null);
      toast.erro(mensagemDeErro(e));
    },
  });
  return (
    <Cartao titulo={t('modal.motivoTitulo')}>
      {s.pode('pipeline.configurar') && (
        <form
          id="motivo-novo"
          className="mb-4 flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (nome.trim()) criar.mutate();
          }}
        >
          <Entrada aria-label={t('campos.nome')} placeholder={t('acoes.novoMotivo')} value={nome} onChange={(e) => setNome(e.target.value)} className="min-w-0 flex-1 basis-40" maxLength={80} />
          <AcaoPrimaria>
            {s.pode('pipeline.excluir') && <BotaoLixeira entidades={['pipeline.motivo_perda']} />}
            <Botao type="submit" form="motivo-novo" variante="primario" icone={<Plus className="size-4" />} carregando={criar.isPending}>
              {t('acoes.adicionar')}
            </Botao>
          </AcaoPrimaria>
        </form>
      )}
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <Vazio texto={t('motivos.vazio')} />
          ) : (
          <ul className="divide-y divide-borda">
            {d.itens.map((m) => (
              <li key={m.id} className="flex items-start justify-between gap-3 py-2 text-sm sm:items-center">
                {editando?.id === m.id ? (
                  <form
                    className="flex min-w-0 flex-1 items-center gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (editando.nome.trim()) renomear.mutate(editando);
                    }}
                  >
                    {/* Review r31 visual (M3): o lápis some; o campo da edição recebe o foco (como em Estruturas). */}
                    <Entrada autoFocus aria-label={t('campos.nome')} value={editando.nome} maxLength={80} onChange={(e) => setEditando({ id: m.id, nome: e.target.value })} />
                    <Botao type="submit" tamanho="sm" variante="fantasma" aria-label={tn('comum.salvar')} carregando={renomear.isPending} icone={<Check className="size-4" />} />
                    <Botao tamanho="sm" variante="fantasma" aria-label={tn('comum.cancelar')} onClick={() => setEditando(null)} icone={<X className="size-4" />} />
                  </form>
                ) : (
                  <>
                    <span className="min-w-0 break-words" title={rotuloMotivoPerda(m.nome)}>{rotuloMotivoPerda(m.nome)}</span>
                    <span className="flex shrink-0 flex-wrap items-center justify-end gap-x-3">
                      <span className="numero text-xs text-tinta-3">{t('motivos.perdidas', { count: m.perdidas })}</span>
                      {s.pode('pipeline.configurar') && <Botao tamanho="sm" variante="fantasma" aria-label={tn('comum.editar')} onClick={() => setEditando({ id: m.id, nome: m.nome })} icone={<Pencil className="size-4" />} />}
                      {s.pode('pipeline.configurar') && <Botao tamanho="sm" variante="fantasma" aria-label={tn('comum.excluir')} onClick={() => setExcluindo(m)} icone={<Trash2 className="size-4" />} />}
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
          )
        }
      </Estado>
      <Confirmar
        aberto={!!excluindo}
        aoFechar={() => setExcluindo(null)}
        aoConfirmar={() => excluindo && remover.mutate(excluindo.id)}
        titulo={t('motivos.confirmarRemover')}
        texto={t('motivos.confirmarTexto')}
        perigoso
        carregando={remover.isPending}
      />
    </Cartao>
  );
}
